import { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient } from '@/services/api';
import { Skeleton } from '@connor-music/shared';
import { Bell, Mail, ChevronDown, ChevronUp, AlertCircle, Users, Zap, Tag } from 'lucide-react';

type NotificationsTab = 'emails';

interface EmailPreview {
  id: string;
  name: string;
  description: string;
  trigger: string;
  recipients: string;
  subject: string;
  html: string;
}

export default function CustomerNotifications() {
  const [activeTab, setActiveTab] = useState<NotificationsTab>('emails');

  const tabs = [
    { key: 'emails' as NotificationsTab, label: 'Customer Notification Emails', icon: Mail },
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-900/50">
          <Bell className="h-5 w-5 text-primary-600 dark:text-primary-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Customer Notifications
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            View email notifications sent to customer users.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="-mb-px flex space-x-6 sm:space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                activeTab === tab.key
                  ? 'border-accent-500 text-accent-600 dark:text-accent-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === 'emails' && <EmailTemplatesSection />}
    </div>
  );
}

// =============================================================================
// Email Templates Section
// =============================================================================

function EmailTemplatesSection() {
  const [previews, setPreviews] = useState<EmailPreview[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const loadPreviews = useCallback(async () => {
    try {
      const data = await apiClient.getEmailPreviews();
      setPreviews(data);
    } catch (err) {
      console.error('Failed to load email previews:', err);
      setError('Failed to load email previews.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPreviews();
  }, [loadPreviews]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Skeleton className="h-9 w-9 rounded-lg" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-3 w-64" />
                </div>
              </div>
              <Skeleton className="h-5 w-5 rounded" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-6 text-center">
        <AlertCircle className="mx-auto h-8 w-8 text-red-400" />
        <p className="mt-2 text-sm text-red-700 dark:text-red-400">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {previews.map((preview) => (
        <EmailPreviewCard key={preview.id} preview={preview} />
      ))}
    </div>
  );
}

// =============================================================================
// Expandable Email Preview Card
// =============================================================================

function EmailPreviewCard({ preview }: { preview: EmailPreview }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Auto-resize iframe to match content height
  useEffect(() => {
    if (!isExpanded || !iframeRef.current) return;

    const iframe = iframeRef.current;
    const handleLoad = () => {
      try {
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (doc?.body) {
          iframe.style.height = doc.body.scrollHeight + 'px';
        }
      } catch {
        // Cross-origin fallback — use fixed height
        iframe.style.height = '600px';
      }
    };

    iframe.addEventListener('load', handleLoad);
    // Trigger if already loaded
    if (iframe.contentDocument?.readyState === 'complete') {
      handleLoad();
    }

    return () => iframe.removeEventListener('load', handleLoad);
  }, [isExpanded]);

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
      {/* Collapsed header — always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-50 dark:bg-blue-900/30 flex-shrink-0">
            <Mail className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
              {preview.name}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {preview.description}
            </p>
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-5 w-5 text-gray-400 flex-shrink-0" />
        ) : (
          <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />
        )}
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <div className="border-t border-gray-200 dark:border-gray-700">
          {/* Metadata */}
          <div className="px-5 py-4 grid grid-cols-1 gap-3 sm:grid-cols-3 border-b border-gray-100 dark:border-gray-700/50">
            <div className="flex items-start gap-2">
              <Zap className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Trigger</p>
                <p className="text-sm text-gray-700 dark:text-gray-300 mt-0.5">{preview.trigger}</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Users className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Recipients</p>
                <p className="text-sm text-gray-700 dark:text-gray-300 mt-0.5">{preview.recipients}</p>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Tag className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Subject Line</p>
                <p className="text-sm text-gray-700 dark:text-gray-300 mt-0.5 font-mono text-xs">{preview.subject}</p>
              </div>
            </div>
          </div>

          {/* Email preview */}
          <div className="p-5">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
              Email Preview
            </p>
            <div className="rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden bg-gray-100 dark:bg-gray-700">
              <iframe
                ref={iframeRef}
                srcDoc={preview.html}
                sandbox=""
                title={`${preview.name} email preview`}
                className="w-full border-0"
                style={{ minHeight: '400px' }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
